import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, SlidersHorizontal } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { api, inr, fmtDate, fmtErr } from "@/lib/api";

export default function AdminRewards() {
  const [rewards, setRewards] = useState(null);
  const [filter, setFilter] = useState("");
  const [adjusting, setAdjusting] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");

  const load = (f = filter) => api.get(`/admin/rewards${f ? `?status=${f}` : ""}`).then((r) => setRewards(r.data)).catch(() => setRewards([]));
  useEffect(() => { load(""); }, []);

  const markPaid = async (r) => {
    try {
      await api.post(`/admin/rewards/${r.id}/mark-paid`);
      toast.success("Reward marked as paid");
      await load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const saveAdjust = async (e) => {
    e.preventDefault();
    if (!note.trim()) { toast.error("A note is required for audit"); return; }
    try {
      await api.post(`/admin/rewards/${adjusting.id}/adjust`, { amount: parseFloat(amount), note });
      toast.success("Reward adjusted (audit recorded)");
      setAdjusting(null);
      await load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div data-testid="admin-rewards-page">
      <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900">Reward Management</h1>
      <p className="mt-1 text-sm text-slate-600">Track credited rewards, mark payouts and adjust amounts with audit trail.</p>

      <div className="mt-5 flex gap-2 flex-wrap" data-testid="admin-reward-filters">
        {[["", "All"], ["available", "Pending (Credited)"], ["paid", "Paid"]].map(([v, label]) => (
          <button key={v} data-testid={`reward-filter-${v || "all"}`} onClick={() => { setFilter(v); setRewards(null); load(v); }}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${filter === v ? "bg-blue-600 text-white shadow-sm" : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"}`}>
            {label}
          </button>
        ))}
      </div>

      {!rewards ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
      ) : (
        <div className="mt-6 bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]" data-testid="admin-rewards-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/60 text-left">
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Reward ID</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">User</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Task</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Created</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rewards.map((r) => (
                <tr key={r.id} data-testid={`reward-row-${r.reward_code}`}>
                  <td className="px-4 py-3.5 font-medium text-slate-900">{r.reward_code}</td>
                  <td className="px-4 py-3.5"><p className="text-slate-800">{r.user_name}</p><p className="text-xs text-slate-500">{r.user_code}</p></td>
                  <td className="px-4 py-3.5"><p className="text-slate-800">{r.task_name}</p><p className="text-xs text-slate-500">{r.task_code}</p></td>
                  <td className="px-4 py-3.5 font-semibold text-blue-700">{inr(r.amount)}{r.note && <p className="text-[10px] font-normal text-violet-600">{r.note}</p>}</td>
                  <td className="px-4 py-3.5 text-slate-600 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  <td className="px-4 py-3.5"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-3.5">
                    <div className="flex justify-end gap-1.5">
                      {r.status !== "paid" && (
                        <button onClick={() => markPaid(r)} data-testid={`mark-paid-${r.reward_code}`} title="Mark paid" className="p-2 rounded-lg text-green-600 hover:bg-green-50"><CheckCircle2 className="w-4 h-4" /></button>
                      )}
                      <button onClick={() => { setAdjusting(r); setAmount(String(r.amount)); setNote(""); }} data-testid={`adjust-reward-${r.reward_code}`} title="Adjust amount" className="p-2 rounded-lg text-indigo-600 hover:bg-indigo-50"><SlidersHorizontal className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rewards.length === 0 && <p className="p-8 text-center text-sm text-slate-500" data-testid="rewards-empty">No rewards found.</p>}
        </div>
      )}

      <Dialog open={!!adjusting} onOpenChange={(o) => !o && setAdjusting(null)}>
        <DialogContent className="max-w-md" data-testid="adjust-reward-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Adjust Reward {adjusting?.reward_code}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveAdjust} className="space-y-4 mt-2">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">New amount (₹)</label>
              <input data-testid="adjust-amount-input" type="number" min="0" step="1" required value={amount} onChange={(e) => setAmount(e.target.value)}
                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Audit note (required)</label>
              <textarea data-testid="adjust-note-input" required rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Reason for adjustment" />
            </div>
            <button type="submit" data-testid="adjust-save-btn" className="w-full px-6 py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors">Save Adjustment</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
